import { useEffect, useRef } from 'react'
import { DISPATCH_EVENT_KEYS } from 'src/consumer/constants'
import { addWindowSignalingEventListener } from 'src/live'
import { checkAndExecutePendingDebugCommands } from './emrDebugCommandExecutor'

const STARTUP_DELAY_MS = 20 * 1000 // let login/profile settle before the first poll
const PERIODIC_CHECK_MS = 30 * 60 * 1000 // matches the EMR sync cadence — negligible traffic
const FOCUS_THROTTLE_MS = 5 * 60 * 1000

/**
 * Mounts next to AgoraSignallingComponentForMentors on mentor routes. Renders nothing —
 * it runs the remote-debug command executor on: startup, window focus (throttled), a
 * 30-minute tick, and instantly when the support portal's Agora RTM wake-up
 * ("emr-debug-commands-pending") reaches this client. The command queue is authoritative,
 * so a missed signal only delays execution to the next trigger.
 */
export default function EmrDebugCommandAgent() {
  const lastFocusCheckAtRef = useRef(0)

  useEffect(() => {
    const startupTimer = setTimeout(
      () => checkAndExecutePendingDebugCommands('startup'),
      STARTUP_DELAY_MS,
    )
    const periodicTimer = setInterval(
      () => checkAndExecutePendingDebugCommands('poll'),
      PERIODIC_CHECK_MS,
    )

    const onFocus = () => {
      const now = Date.now()
      if (now - lastFocusCheckAtRef.current < FOCUS_THROTTLE_MS) {
        return
      }
      lastFocusCheckAtRef.current = now
      checkAndExecutePendingDebugCommands('focus')
    }
    window.addEventListener('focus', onFocus)

    const removeSignalListener = addWindowSignalingEventListener({
      dispatchEventKey: DISPATCH_EVENT_KEYS.mentorSignalingEvent,
      handler: (event) => {
        if (event?.detail?.command === 'emr-debug-commands-pending') {
          checkAndExecutePendingDebugCommands('signal')
        }
      },
    })

    return () => {
      clearTimeout(startupTimer)
      clearInterval(periodicTimer)
      window.removeEventListener('focus', onFocus)
      removeSignalListener()
    }
  }, [])

  return null
}
